import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
//
import { useLocation, useNavigate } from 'react-router-dom';
import AppRoute, { type IRoute } from '../../../config/config.route';
import services from '../../../app/config/services';
import AuthControls from '../../../commons/components/AuthControls/AuthControls';
import { useAppSelector } from '../../../reduxStore/utils/reduxHooks';
import { canAccessRoute, safeRole } from '../../../commons/utils/rbac';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const EasyDrawer = () => {
  //
  const navigate = useNavigate();
  const location = useLocation();
  const { role, rolePages } = useAppSelector((state) => state.auth);
  //
  const [open, setOpen] = React.useState(false);
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleListItemClick = (rt: IRoute) => (): void => {
    navigate(`/${services.getLocale()}${rt.path}`);
  };

  const isActive = (rt: IRoute) => (
    location.pathname.endsWith(rt.path)
    || location.pathname.endsWith(`${rt.path}/`)
  );

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader>
        <IconButton
          onClick={open ? handleDrawerClose : handleDrawerOpen}
          sx={{
            color: '#F8FAFC',
            '&:hover': {
              backgroundColor: 'rgba(248, 250, 252, 0.12)',
            },
          }}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          px: open ? 2 : 1,
          py: 1.5,
        }}
      >
        <Box
          component="img"
          src={open ? '/logo-white.svg' : '/logo-mark-white.svg'}
          alt="Daoyidh Webdev"
          sx={{
            height: open ? 28 : 28,
            width: 'auto',
            filter: 'drop-shadow(0 2px 8px rgba(37, 99, 235, 0.35))',
          }}
        />
      </Box>
      <Divider />
      <List>
        {AppRoute
          .filter((r) => r.showInMenu)
          .filter((r) => canAccessRoute(r, safeRole(role), rolePages))
          .map((rt) => (
            <ListItem key={rt.id} disablePadding sx={{ display: 'block' }}>
              <Tooltip title={rt.label} placement="right" disableHoverListener={open}>
                <ListItemButton
                  selected={isActive(rt)}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: open ? 2.5 : 1,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 6,
                      bottom: 6,
                      width: 3,
                      borderRadius: 6,
                      backgroundColor: 'primary.main',
                      opacity: isActive(rt) ? 1 : 0,
                      transition: 'opacity 0.2s ease',
                    },
                  }}
                  onClick={handleListItemClick(rt)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 2 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isActive(rt)
                          ? 'rgba(37, 99, 235, 0.4)'
                          : 'rgba(248, 250, 252, 0.12)',
                      }}
                    >
                      <rt.icon fontSize="small" sx={{ color: '#F8FAFC' }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={rt.label}
                    sx={{
                      opacity: open ? 1 : 0,
                      color: '#F8FAFC',
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
      </List>
      <Divider />
      <AuthControls compact={!open} />
    </Drawer>
  );
};

export default EasyDrawer;
